import type { IntegrationItemDto } from '@fos/shared/contracts/integrations';
import {
  MAIL_SIGNATURE_CONFIG_KEY,
  MAIL_SIGNATURE_INPUT_MAX_CHARS,
  MAIL_SIGNATURE_MESSAGES,
  sanitizeSignatureHtml,
} from '@fos/shared/mail-signature';
import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { errorMessage, validationIssues } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Textarea } from '../../../ui/Input';
import { toast } from '../../../ui/Toast';

/** Zapisany podpis skrzynki (konfiguracja integracji; `null` = brak). */
export function mailboxSignature(item: Pick<IntegrationItemDto, 'config'>): string | null {
  const v = item.config[MAIL_SIGNATURE_CONFIG_KEY];
  return typeof v === 'string' && v.trim() ? v : null;
}

/**
 * Podgląd podpisu: ta sama sanitizacja co na serwerze, w ramce bez skryptów i bez dostępu do aplikacji
 * (`sandbox=""`). Zdalne obrazy (logo) są w podglądzie zastąpione ramką z opisem — polityka bezpieczeństwa aplikacji
 * nie wczytuje obrazów z innych domen; w mailu pojawią się normalnie.
 */
export function signaturePreviewDoc(clean: string): string {
  const withPlaceholders = clean.replace(/<img\b([^>]*)>/g, (_m, attrs: string) => {
    const alt = /\balt="([^"]*)"/.exec(attrs)?.[1] ?? 'obraz';
    const w = /\bwidth="([0-9]{1,4})"/.exec(attrs)?.[1];
    const h = /\bheight="([0-9]{1,4})"/.exec(attrs)?.[1];
    const size = `${w ? `width:${w}px;` : ''}${h ? `height:${h}px;` : ''}`;
    return `<span style="display:inline-block;${size}min-width:40px;border:1px dashed #b9bec8;border-radius:4px;color:#7b808c;font:11px/1.2 Arial,sans-serif;padding:2px 4px;box-sizing:border-box;overflow:hidden">${alt}</span>`;
  });
  return `<!DOCTYPE html><html><head><meta charset="utf-8"></head><body style="margin:12px;background:#fff;font-family:Arial,Helvetica,sans-serif;font-size:14px">${withPlaceholders}</body></html>`;
}

export function MailSignatureDialog({
  open,
  onOpenChange,
  item,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: IntegrationItemDto;
}) {
  const utils = trpc.useUtils();
  const save = trpc.mail.setSignature.useMutation();
  const [html, setHtml] = useState('');
  const [error, setError] = useState<string | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: wartość początkowa przy otwarciu
  useEffect(() => {
    if (!open) return;
    setHtml(mailboxSignature(item) ?? '');
    setError(null);
  }, [open]);

  const clean = useMemo(() => (html.trim() ? sanitizeSignatureHtml(html) : ''), [html]);
  const saved = mailboxSignature(item);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!item.id) return;
    setError(null);
    try {
      const res = await save.mutateAsync({ integrationId: item.id, signatureHtml: html });
      toast.success(res.signatureHtml ? MAIL_SIGNATURE_MESSAGES.saved : MAIL_SIGNATURE_MESSAGES.removed);
      await utils.integrations.list.invalidate();
      onOpenChange(false);
    } catch (err) {
      setError(validationIssues(err)[0]?.message ?? errorMessage(err, 'Nie udało się zapisać podpisu.'));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="l"
      title="Podpis maila"
      description={item.summary ?? undefined}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={save.isPending}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" form="mail-signature-form" loading={save.isPending}>
            {!html.trim() && saved ? 'Usuń podpis' : 'Zapisz podpis'}
          </Button>
        </>
      }
    >
      <form id="mail-signature-form" onSubmit={submit} className="flex flex-col gap-4" noValidate>
        {error && <FormMessage>{error}</FormMessage>}
        <Field label="Podpis (HTML)" hint={MAIL_SIGNATURE_MESSAGES.hint}>
          <Textarea
            value={html}
            onChange={(e) => setHtml(e.target.value)}
            rows={6}
            maxLength={MAIL_SIGNATURE_INPUT_MAX_CHARS}
            spellCheck={false}
            className="font-mono text-caption"
            placeholder='<table cellpadding="0" cellspacing="0">…</table>'
          />
        </Field>
        <div className="flex flex-col gap-1.5">
          <span className="text-body-s font-medium text-fg">Podgląd</span>
          {clean ? (
            <iframe
              title="Podgląd podpisu"
              sandbox=""
              srcDoc={signaturePreviewDoc(clean)}
              className="h-44 w-full rounded-lg border border-line bg-panel"
            />
          ) : (
            <p className="rounded-lg border border-line px-4 py-3 text-body-s text-fg-muted">
              Brak podpisu — maile wyjdą bez niego.
            </p>
          )}
          <p className="text-caption text-fg-muted">
            Podpis trafi pod treść każdego maila z tej skrzynki (także szkiców AI). Obrazy z sieci widać w mailu.
          </p>
        </div>
      </form>
    </Dialog>
  );
}
